import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeadlineRulesInput from './DeadlineRulesInput';
import {
  AUDIT_REQUIREMENT_OPTIONS,
  FILING_STATUS_OPTIONS,
  FORM_990_TYPE_OPTIONS,
  type AuditThresholdTier,
  type DeadlineRule,
  type FilingStatus,
  type FilingStatusRule,
  type FilingWindow,
  type Form990Type,
  type PartIVRule,
} from '../lib/deadlineRules';

// Structured editors for the nested reference-data shapes. These replace raw
// JSON textareas, which were workable but put the burden of remembering each
// schema on whoever was entering state data.

function numberOrUndef(raw: string): number | undefined {
  if (raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Shared frame: a titled, outlined block with an explanation. */
function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700}>
        {title}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hint}
        </Typography>
      )}
      {children}
    </Box>
  );
}

/** A removable card in a repeating list. */
function Row({ label, onRemove, children }: { label: string; onRemove: () => void; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography variant="caption" fontWeight={700}>
          {label}
        </Typography>
        <Box flexGrow={1} />
        <IconButton size="small" onClick={onRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      {children}
    </Paper>
  );
}

// ─── Filing window (868ktk58c) ────────────────────────────────────────────

export function FilingWindowInput({
  value,
  onChange,
}: {
  value: FilingWindow | undefined;
  onChange: (next: FilingWindow | undefined) => void;
}) {
  const win = value ?? {};
  const pt = win.processingTime ?? {};

  const set = (patch: Partial<FilingWindow>) => {
    const next = { ...win, ...patch };
    const empty =
      (!next.opensRules || next.opensRules.length === 0) &&
      !next.notes &&
      (!next.processingTime || Object.values(next.processingTime).every((v) => v === undefined || v === ''));
    onChange(empty ? undefined : next);
  };

  const setPt = (patch: Partial<NonNullable<FilingWindow['processingTime']>>) =>
    set({ processingTime: { ...pt, ...patch } });

  return (
    <Stack spacing={2}>
      <Section
        title="Window opens"
        hint="When the filing can first be submitted — the deadline rules above say when it is late. Uses the same date options. Where several are listed the earliest wins, since the window is open once any of them says so."
      >
        <DeadlineRulesInput
          value={win.opensRules ?? []}
          onChange={(rules: DeadlineRule[]) => set({ opensRules: rules })}
        />
      </Section>

      <Section title="Processing time" hint="How long the agency takes once the filing is submitted. Used to set client expectations.">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            type="number"
            size="small"
            label="Min days"
            value={pt.minDays ?? ''}
            onChange={(e) => setPt({ minDays: numberOrUndef(e.target.value) })}
            sx={{ width: 130 }}
          />
          <TextField
            type="number"
            size="small"
            label="Max days"
            value={pt.maxDays ?? ''}
            onChange={(e) => setPt({ maxDays: numberOrUndef(e.target.value) })}
            sx={{ width: 130 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={pt.businessDays !== false}
                onChange={(_, checked) => setPt({ businessDays: checked })}
              />
            }
            label="Business days"
          />
        </Stack>
        <TextField
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          label="Processing notes"
          value={pt.notes ?? ''}
          onChange={(e) => setPt({ notes: e.target.value || undefined })}
        />
      </Section>

      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        label="Filing window notes"
        value={win.notes ?? ''}
        onChange={(e) => set({ notes: e.target.value || undefined })}
      />
    </Stack>
  );
}

// ─── Past-due / revoked rules (868ktk557) ─────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  current: 'Current',
  pastDue: 'Past due',
  revoked: 'Revoked',
};

export function FilingStatusRulesInput({
  value,
  onChange,
}: {
  value: FilingStatusRule[] | undefined;
  onChange: (next: FilingStatusRule[] | undefined) => void;
}) {
  const rules = value ?? [];
  const set = (next: FilingStatusRule[]) => onChange(next.length > 0 ? next : undefined);
  const updateAt = (idx: number, patch: Partial<FilingStatusRule>) =>
    set(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  // 'current' is the record's ordinary state, already described by its own
  // fields, so only the out-of-standing statuses are offered here.
  const available = FILING_STATUS_OPTIONS.filter(
    (o) => o.value !== 'current' && !rules.some((r) => r.status === o.value)
  );

  return (
    <Stack spacing={1.5}>
      {rules.map((rule, idx) => (
        <Row key={rule.status} label={`When ${STATUS_LABEL[rule.status] ?? rule.status}`} onRemove={() => set(rules.filter((_, i) => i !== idx))}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                label="Reinstatement form"
                value={rule.reinstatementForm ?? ''}
                onChange={(e) => updateAt(idx, { reinstatementForm: e.target.value || undefined })}
                sx={{ minWidth: 200, flex: 1 }}
              />
              <TextField
                size="small"
                label="Reinstatement fee"
                value={rule.reinstatementFee ?? ''}
                onChange={(e) => updateAt(idx, { reinstatementFee: e.target.value || undefined })}
                sx={{ width: 170 }}
              />
            </Stack>
            <TextField
              size="small"
              fullWidth
              label="Penalty / late fee structure"
              value={rule.penalty ?? ''}
              onChange={(e) => updateAt(idx, { penalty: e.target.value || undefined })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!rule.backFilingRequired}
                  onChange={(_, checked) => updateAt(idx, { backFilingRequired: checked })}
                />
              }
              label="Missed prior periods must also be filed"
            />
            {rule.backFilingRequired && (
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                label="Back-filing notes"
                value={rule.backFilingNotes ?? ''}
                onChange={(e) => updateAt(idx, { backFilingNotes: e.target.value || undefined })}
              />
            )}
            <Section
              title="Replacement deadlines"
              hint="Optional. Deadlines that apply instead of the standard ones while in this status."
            >
              <DeadlineRulesInput
                value={rule.deadlineRules ?? []}
                onChange={(next: DeadlineRule[]) =>
                  updateAt(idx, { deadlineRules: next.length > 0 ? next : undefined })
                }
              />
            </Section>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={rule.notes ?? ''}
              onChange={(e) => updateAt(idx, { notes: e.target.value || undefined })}
            />
          </Stack>
        </Row>
      ))}

      {available.length > 0 && (
        <Stack direction="row" spacing={1}>
          {available.map((o) => (
            <Button
              key={o.value}
              size="small"
              startIcon={<AddIcon />}
              onClick={() => set([...rules, { status: o.value as FilingStatus }])}
            >
              Add “{o.label}” rules
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

// ─── Part IV rules per 990 type (868ktk5jw) ───────────────────────────────

export function PartIVRulesInput({
  value,
  onChange,
}: {
  value: PartIVRule[] | undefined;
  onChange: (next: PartIVRule[] | undefined) => void;
}) {
  const rules = value ?? [];
  const set = (next: PartIVRule[]) => onChange(next.length > 0 ? next : undefined);
  const updateAt = (idx: number, patch: Partial<PartIVRule>) =>
    set(rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const available = FORM_990_TYPE_OPTIONS.filter((o) => !rules.some((r) => r.form === o.value));

  return (
    <Stack spacing={1.5}>
      {rules.map((rule, idx) => (
        <Row key={rule.form} label={rule.form} onRemove={() => set(rules.filter((_, i) => i !== idx))}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Form"
              value={rule.form}
              onChange={(e) => updateAt(idx, { form: e.target.value as Form990Type })}
              sx={{ width: 160 }}
            >
              {FORM_990_TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value} disabled={rules.some((r) => r.form === o.value && r.form !== rule.form)}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Part IV line(s)"
              placeholder="e.g. 11a or 25a, 25b"
              value={rule.line ?? ''}
              onChange={(e) => updateAt(idx, { line: e.target.value || undefined })}
              sx={{ width: 200 }}
            />
          </Stack>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            sx={{ mt: 1 }}
            label="Required when"
            value={rule.requiredWhen ?? ''}
            onChange={(e) => updateAt(idx, { requiredWhen: e.target.value || undefined })}
          />
        </Row>
      ))}

      {available.length > 0 && (
        <Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => set([...rules, { form: available[0].value }])}
          >
            Add rule for {available[0].label}
          </Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Part IV triggers differ per 990 variant, so give each form its own line and condition.
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ─── Audit thresholds (868ktk5fu) ─────────────────────────────────────────

export function AuditTiersInput({
  value,
  onChange,
}: {
  value: AuditThresholdTier[] | undefined;
  onChange: (next: AuditThresholdTier[] | undefined) => void;
}) {
  const tiers = value ?? [];
  const set = (next: AuditThresholdTier[]) => onChange(next.length > 0 ? next : undefined);
  const updateAt = (idx: number, patch: Partial<AuditThresholdTier>) =>
    set(tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));

  const labelFor = (t: AuditThresholdTier) =>
    AUDIT_REQUIREMENT_OPTIONS.find((o) => o.value === t.requirement)?.label ?? t.requirement;

  return (
    <Stack spacing={1.5}>
      {tiers.map((tier, idx) => (
        <Row key={idx} label={labelFor(tier)} onRemove={() => set(tiers.filter((_, i) => i !== idx))}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField
                select
                size="small"
                label="Requirement"
                value={tier.requirement}
                onChange={(e) => updateAt(idx, { requirement: e.target.value as AuditThresholdTier['requirement'] })}
                sx={{ minWidth: 220 }}
              >
                {AUDIT_REQUIREMENT_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="number"
                size="small"
                label="Revenue at or above"
                value={tier.thresholdRevenue ?? ''}
                onChange={(e) => updateAt(idx, { thresholdRevenue: numberOrUndef(e.target.value) })}
                sx={{ width: 180 }}
              />
              <TextField
                type="number"
                size="small"
                label="And below (optional)"
                helperText="Leave blank if the requirement has no upper bound"
                value={tier.thresholdRevenueMax ?? ''}
                onChange={(e) => updateAt(idx, { thresholdRevenueMax: numberOrUndef(e.target.value) })}
                sx={{ width: 200 }}
              />
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <TextField
                size="small"
                label="Form"
                value={tier.form ?? ''}
                onChange={(e) => updateAt(idx, { form: e.target.value || undefined })}
                sx={{ width: 180 }}
              />
              <TextField
                size="small"
                label="Agency"
                value={tier.agency ?? ''}
                onChange={(e) => updateAt(idx, { agency: e.target.value || undefined })}
                sx={{ minWidth: 220, flex: 1 }}
              />
            </Stack>
            <TextField
              size="small"
              fullWidth
              label="Notes"
              value={tier.notes ?? ''}
              onChange={(e) => updateAt(idx, { notes: e.target.value || undefined })}
            />
          </Stack>
        </Row>
      ))}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => set([...tiers, { requirement: 'audit' }])}
        >
          Add threshold
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          One entry per requirement — a state may set a different revenue threshold for each, such
          as a review above $500,000, compiled statements above $750,000, and a full audit above
          $1,000,000.
        </Typography>
      </Box>
    </Stack>
  );
}
